'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FaTrash,
  FaPalette,
  FaFemale,
  FaMagic,
  FaCheck,
  FaTimes,
  FaPencilAlt,
} from 'react-icons/fa'
import { BsFillChatLeftDotsFill, BsChatLeftDots } from 'react-icons/bs'
import { BsStack } from 'react-icons/bs'
import { getModelName, truncate } from '../helpers/helpers'
import { asLiveModel } from '../contexts/CreateContext'
import { ClipLoader } from 'react-spinners'
import {
  deleteSavedPrompt,
  listSavedPrompts,
  renameSavedPrompt,
} from '../app/(chatAndCreateContext)/generate/api'

const BookmarkModalContent = ({
  user,

  setModel,
  setPrompt,
  setNegativePrompt,
  setLora,
  setLorasList,
  setCreativity,
  setImageSize,
  setSeed,
  setModalVisible,
  modalVisible,
  setQuality,
  setDetails,
  setAge,
  setWeight,
  setBreasts,
  setAss,
  setFilter,
  setFaceFix,
  setSteps,
  setWidth,
  setHeight,
  setExpression,
  setRestoreFaces,
  setVideo,

  video,
}) => {
  const [savedPrompts, setSavedPrompts] = useState([])
  const [fetchingPrompts, setFetchingPrompts] = useState(true)

  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState('')
  const [originalName, setOriginalName] = useState('')

  const [search, setSearch] = useState('')

  const fetchSavedPrompts = useCallback(async () => {
    setFetchingPrompts(true)

    if (!user) {
      setFetchingPrompts(false)
      return
    }

    try {
      const idToken = await user.getIdToken()

      const response = await listSavedPrompts(
        idToken,
        typeof video === 'boolean' ? video : undefined,
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to fetch saved prompts')
      }

      const prompts = await response.json()
      setSavedPrompts(prompts)
    } catch (error) {
      console.error(error.message)
    } finally {
      setFetchingPrompts(false)
    }
  }, [user, video])

  const renamePrompt = async (promptId, name) => {
    const clean = (name || '').trim()
    if (!clean) return

    try {
      const idToken = await user.getIdToken()

      const res = await renameSavedPrompt(idToken, promptId, clean)

      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'Rename failed')
      }

      setSavedPrompts((prev) =>
        prev.map((p) => (p.id === promptId ? { ...p, name: clean } : p)),
      )
    } catch (err) {
      console.error(err.message)
      setEditingName(originalName)
    }
  }

  const startRename = (p) => {
    const current = p.name || truncate(p.prompt || '', 34) || ''
    setEditingId(p.id)
    setEditingName(current)
    setOriginalName(current)
  }

  const cancelRename = () => {
    setEditingName(originalName)
    setEditingId(null)
  }

  const saveRename = async (p) => {
    await renamePrompt(p.id, editingName)
    setEditingId(null)
  }

  const deletePrompt = async (promptId) => {
    try {
      const idToken = await user.getIdToken()
      const response = await deleteSavedPrompt(idToken, promptId)

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete prompt')
      }

      fetchSavedPrompts()
    } catch (error) {
      console.error(error.message)
    }
  }

  useEffect(() => {
    fetchSavedPrompts()
  }, [modalVisible, fetchSavedPrompts])

  useEffect(() => {
    setSearch('')
  }, [modalVisible, video])

  const clonePrompt = (p) => {
    if (p.model !== undefined) setModel(asLiveModel.decode(p.model))
    if (p.prompt !== undefined) setPrompt(p.prompt)

    if (p.negative_prompt !== undefined) setNegativePrompt(p.negative_prompt)
    else if (p.negativePrompt !== undefined) setNegativePrompt(p.negativePrompt)

    if (p.loras !== undefined) setLora(p.loras)
    if (p.loras_list !== undefined) setLorasList(p.loras_list)

    if (p.cfg_scale !== undefined) setCreativity(p.cfg_scale)
    if (p.seed !== undefined) setSeed(p.seed)
    if (p.quality !== undefined) setQuality?.(p.quality)
    if (p.details !== undefined) setDetails?.(p.details)
    if (p.steps !== undefined) setSteps?.(p.steps)

    if (p.width !== undefined && p.height !== undefined) {
      setImageSize?.(`${p.width}x${p.height}`)
    }

    if (p.width !== undefined) setWidth?.(p.width)
    if (p.height !== undefined) setHeight?.(p.height)

    if (p.age_slider !== undefined) setAge?.(p.age_slider)
    if (p.weight_slider !== undefined) setWeight?.(p.weight_slider)
    if (p.breast_slider !== undefined) setBreasts?.(p.breast_slider)
    if (p.ass_slider !== undefined) setAss?.(p.ass_slider)

    if (p.style_filter !== undefined) setFilter?.(p.style_filter)
    if (p.expression !== undefined) setExpression?.(p.expression)

    if (p.restore_faces !== undefined) {
      setFaceFix?.(p.restore_faces)
      setRestoreFaces?.(p.restore_faces)
    }

    if (p.video !== undefined) setVideo?.(p.video)

    setModalVisible(false)
  }

  const formatSavedAt = (savedAt) => {
    if (!savedAt) return 'Unknown'
    try {
      let d
      if (typeof savedAt === 'string') d = new Date(savedAt)
      else if (typeof savedAt === 'number') d = new Date(savedAt)
      else if (savedAt?.seconds) d = new Date(savedAt.seconds * 1000)

      if (!d || isNaN(d.getTime())) return 'Unknown'
      return d.toLocaleDateString()
    } catch {
      return 'Unknown'
    }
  }

  const query = search.trim().toLowerCase()

  const filteredPrompts = useMemo(() => {
    if (!query) return savedPrompts
    return (savedPrompts || []).filter((p) => {
      const name = (p.name || '').toLowerCase()
      const prompt = (p.prompt || '').toLowerCase()
      return name.includes(query) || prompt.includes(query)
    })
  }, [savedPrompts, query])

  const total = savedPrompts?.length || 0
  const shownTotal = filteredPrompts?.length || 0
  const hasSearch = !!query

  return (
    <div className="">
      <div
        className="mx-auto min-h-[460px] flex flex-col"
        style={{ width: '48rem', maxWidth: '100%' }}
      >
        {fetchingPrompts ? (
          <div className="flex justify-center items-center w-full h-32">
            <ClipLoader color="#eeeeee" size={18} />
          </div>
        ) : total === 0 ? (
          <div className="mt-6 text-gray-400 text-sm text-center w-full">
            You haven’t saved any workflows yet.
          </div>
        ) : (
          <>
            {/* Search bar locked to top (sticky) */}
            <div className="sticky top-0 z-10 pt-2 pb-2 bg-[var(--middle-bg-color)]">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="appearance-none border border-[var(--foreground-bg-color)] rounded-md px-3 py-2 w-full text-white bg-[var(--middle-bg-color)] focus:border-white focus:outline-none text-sm"
                aria-label="Search saved workflows"
              />
            </div>

            {hasSearch && shownTotal === 0 ? (
              <div className="mt-6 text-gray-400 text-sm text-center w-full">
                No workflows match “{search.trim()}”.
              </div>
            ) : (
              <div className="mt-3 overflow-auto max-h-[460px] pr-1 w-full">
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-items-start content-start">
                  {filteredPrompts.map((p, i) => {
                    const savedAtText = formatSavedAt(p.saved_at)
                    const negative = p.negative_prompt ?? p.negativePrompt

                    const isEditing = editingId === p.id

                    return (
                      <div
                        key={p.id || i}
                        className="w-full rounded-2xl border border-[var(--foreground-bg-color)] bg-[var(--middle-bg-color)] shadow-sm overflow-hidden"
                      >
                        <div className="p-3">
                          <div className="min-w-0">
                            {isEditing ? (
                              <input
                                autoFocus
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                className="appearance-none border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[var(--middle-bg-color)] focus:border-white focus:outline-none text-sm"
                                aria-label="Rename saved workflow"
                              />
                            ) : (
                              <div className="text-sm font-semibold text-white truncate">
                                {p.name ||
                                  truncate(p.prompt || '', 34) ||
                                  'Untitled prompt'}
                              </div>
                            )}

                            <div className="mt-0.5 text-[11px] text-gray-400">
                              {savedAtText}
                            </div>
                          </div>

                          <div className="mt-2 space-y-1.5 text-xs text-gray-300">
                            <div className="flex gap-1.5">
                              <BsFillChatLeftDotsFill className="text-gray-400 shrink-0 mt-[2px]" />
                              <span className="line-clamp-3">
                                {p.prompt || '—'}
                              </span>
                            </div>

                            <div className="flex gap-1.5">
                              <BsChatLeftDots className="text-gray-400 shrink-0 mt-[2px]" />
                              <span className="line-clamp-2">
                                {negative || '—'}
                              </span>
                            </div>
                          </div>

                          <div className="mt-2.5 flex flex-wrap gap-1.5">
                            {p.model && (
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] text-white bg-[var(--foreground-bg-color)]"
                                title={p.model}
                              >
                                <FaPalette size={11} />
                                <span className="leading-none whitespace-nowrap">
                                  {truncate(
                                    getModelName(p.model, video, p.version) ||
                                      p.model,
                                    16,
                                  )}
                                </span>
                              </span>
                            )}

                            {p.quality !== undefined && (
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] text-white bg-[var(--foreground-bg-color)]"
                                title="Quality"
                              >
                                <BsStack size={11} />
                                <span className="leading-none whitespace-nowrap">
                                  {p.quality}
                                </span>
                              </span>
                            )}

                            {p.loras && (
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] text-white bg-[var(--foreground-bg-color)]"
                                title={truncate(p.loras, 32)}
                              >
                                <FaFemale size={11} />
                                <span className="leading-none whitespace-nowrap">
                                  {truncate(p.loras, 16)}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="px-3 pb-3 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              clonePrompt(p)
                            }}
                            disabled={isEditing}
                            className="flex-1 px-3 py-2 rounded-md focus:outline-none bg-[var(--main-bg-color)] text-white font-semibold text-sm flex items-center justify-center disabled:opacity-50"
                            aria-label="Clone workflow"
                            title="Clone workflow"
                          >
                            <FaMagic className="mr-2" size={12} />
                            Clone
                          </button>

                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  saveRename(p)
                                }}
                                className="w-[38px] h-[38px] inline-flex items-center justify-center rounded-md focus:outline-none text-white bg-[var(--foreground-bg-color)]"
                                title="Save"
                                aria-label="Save"
                              >
                                <FaCheck size={13} />
                              </button>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  cancelRename()
                                }}
                                className="w-[38px] h-[38px] inline-flex items-center justify-center rounded-md focus:outline-none text-white bg-[var(--foreground-bg-color)]"
                                title="Cancel"
                                aria-label="Cancel"
                              >
                                <FaTimes size={13} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  startRename(p)
                                }}
                                className="w-[38px] h-[38px] inline-flex items-center justify-center rounded-md focus:outline-none text-white bg-[var(--foreground-bg-color)]"
                                title="Rename"
                                aria-label="Rename"
                              >
                                <FaPencilAlt size={12} />
                              </button>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  deletePrompt(p.id)
                                }}
                                className="w-[38px] h-[38px] inline-flex items-center justify-center rounded-md focus:outline-none bg-[var(--foreground-bg-color)] text-red-200"
                                title="Delete"
                                aria-label="Delete"
                              >
                                <FaTrash size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default BookmarkModalContent
